<template>
  <AppModal
    :show="visible"
    :title="isEditing ? $t('issues.editIssue') : $t('issues.addIssue')"
    :icon="isEditing ? 'edit' : 'plus'"
    size="large"
    @close="$emit('close')"
  >
    <!-- Form Content -->
    <div class="issue-form">
      <form @submit.prevent="handleSubmit">
        <div class="issue-form__grid">
          
          <!-- Issue Title -->
          <div class="app-form-field app-form-field--full">
            <label class="app-form-field__label app-form-field__label--required">
              <svg viewBox="0 0 24 24" width="18" height="18" class="app-form-field__icon">
                <path fill="currentColor" d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z"/>
              </svg>
              {{ t('issues.title') }}
            </label>
            <div class="app-form-field__wrapper">
              <input
                v-model="formData.title"
                type="text"
                class="app-form-field__input"
                :class="{ 'app-form-field__input--error': errors.title }"
                :placeholder="t('issues.placeholders.enterTitle')"
                maxlength="100"
                required
              />
            </div>
            <div v-if="errors.title" class="app-form-field__error">
              <svg viewBox="0 0 24 24" width="16" height="16">
                <path fill="currentColor" d="M13 14H11V9H13M13 18H11V16H13M1 21H23L12 2L1 21Z"/>
              </svg>
              {{ errors.title }}
            </div>
          </div>

          <!-- Issue Description -->
          <div class="app-form-field app-form-field--full">
            <label class="app-form-field__label">
              <svg viewBox="0 0 24 24" width="18" height="18" class="app-form-field__icon">
                <path fill="currentColor" d="M14,17H7V15H14M17,13H7V11H17M17,9H7V7H17M19,3H5C3.89,3 3,3.89 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V5C21,3.89 20.1,3 19,3Z"/>
              </svg>
              {{ t('issues.description') }}
            </label>
            <div class="app-form-field__wrapper">
              <textarea
                v-model="formData.description"
                class="app-form-field__input app-form-field__input--textarea"
                :placeholder="t('issues.placeholders.enterDescription')"
                rows="4"
                maxlength="1000"
              ></textarea>
            </div>
          </div>

          <!-- Status and Priority in same row -->
          <div class="app-form-field app-form-field--full">
            <div class="issue-form__row">
              <div class="app-form-field">
                <label class="app-form-field__label app-form-field__label--required">
                  <svg viewBox="0 0 24 24" width="18" height="18" class="app-form-field__icon">
                    <path fill="currentColor" d="M12,2L15.09,8.26L22,9.27L17,14.14L18.18,21.02L12,17.77L5.82,21.02L7,14.14L2,9.27L8.91,8.26L12,2Z"/>
                  </svg>
                  {{ t('issues.priority') }}
                </label>
                <div class="app-form-field__wrapper">
                  <select
                    v-model="formData.priority"
                    class="app-form-field__input app-form-field__input--select"
                    required
                  >
                    <option value="LOW">{{ t('issues.priorities.LOW') }}</option>
                    <option value="MEDIUM">{{ t('issues.priorities.MEDIUM') }}</option>
                    <option value="HIGH">{{ t('issues.priorities.HIGH') }}</option>
                    <option value="CRITICAL">{{ t('issues.priorities.CRITICAL') }}</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          <!-- Assigned To Intervenant -->
          <div class="app-form-field app-form-field--full">
            <div class="issue-form__row">
              <div class="app-form-field">
                <label class="app-form-field__label">
                  <svg viewBox="0 0 24 24" width="18" height="18" class="app-form-field__icon">
                    <path fill="currentColor" d="M12,4A4,4 0 0,1 16,8A4,4 0 0,1 12,12A4,4 0 0,1 8,8A4,4 0 0,1 12,4M12,14C16.42,14 20,15.79 20,18V20H4V18C4,15.79 7.58,14 12,14Z"/>
                  </svg>
                  {{ t('issues.assignedTo') }}
                </label>
                <div class="app-form-field__wrapper">
                  <select
                    v-model="formData.assignedTo"
                    class="app-form-field__input app-form-field__input--select"
                  >
                    <option :value="null">{{ t('issues.placeholders.selectIntervenant') }}</option>
                    <option v-for="intervenant in intervenants" :key="intervenant.id" :value="intervenant.id">
                      {{ intervenant.name }}
                    </option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          <!-- Reported Date -->
          <div class="app-form-field app-form-field--full">
            <div class="issue-form__row">
              <div class="app-form-field" v-if="showResolvedDate">
                <label class="app-form-field__label">
                  <svg viewBox="0 0 24 24" width="18" height="18" class="app-form-field__icon">
                    <path fill="currentColor" d="M19,19H5V8H19M16,1V3H8V1H6V3H5C3.89,3 3,3.89 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V5C21,3.89 20.1,3 19,3H18V1M17,12H12V17H17V12Z"/>
                  </svg>
                  {{ t('issues.resolvedDate') }}
                </label>
                <div class="app-form-field__wrapper">
                  <input
                    v-model="formData.resolvedDate"
                    type="date"
                    class="app-form-field__input app-form-field__input--date"
                    :min="formData.resolvedDate"
                  />
                </div>
              </div>
            </div>
          </div>

          <!-- Resolution Notes (only show when status is RESOLVED or CLOSED) -->
          <div class="app-form-field app-form-field--full" v-if="showResolutionNotes">
            <label class="app-form-field__label" :class="{ 'app-form-field__label--required': formData.status === 'RESOLVED' }">
              <svg viewBox="0 0 24 24" width="18" height="18" class="app-form-field__icon">
                <path fill="currentColor" d="M14,17H7V15H14M17,13H7V11H17M17,9H7V7H17M19,3H5C3.89,3 3,3.89 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V5C21,3.89 20.1,3 19,3Z"/>
              </svg>
              {{ t('issues.resolutionNotes') }}
            </label>
            <div class="app-form-field__wrapper">
              <textarea
                v-model="formData.resolutionNotes"
                class="app-form-field__input app-form-field__input--textarea"
                :placeholder="t('issues.placeholders.enterResolutionNotes')"
                rows="3"
                maxlength="500"
              ></textarea>
            </div>
          </div>

          <!-- Photo Upload Section -->
          <div class="app-form-field app-form-field--full">
            <label class="app-form-field__label">
              <svg viewBox="0 0 24 24" width="18" height="18" class="app-form-field__icon">
                <path fill="currentColor" d="M4,4H7L9,2H15L17,4H20A2,2 0 0,1 22,6V18A2,2 0 0,1 20,20H4A2,2 0 0,1 2,18V6A2,2 0 0,1 4,4M12,7A5,5 0 0,0 7,12A5,5 0 0,0 12,17A5,5 0 0,0 17,12A5,5 0 0,0 12,7M12,9A3,3 0 0,1 15,12A3,3 0 0,1 12,15A3,3 0 0,1 9,12A3,3 0 0,1 12,9Z"/>
              </svg>
              {{ t('issues.photos') }}
            </label>
            
            <!-- Upload Area -->
            <div 
              class="photo-upload-area"
              :class="{ 'photo-upload-area--dragging': isDragging }"
              @click="triggerFileInput"
              @dragover.prevent="isDragging = true"
              @dragleave.prevent="isDragging = false"
              @drop.prevent="handleFileDrop"
            >
              <input
                ref="fileInput"
                type="file"
                accept="image/*"
                multiple
                class="photo-upload-input"
                @change="handleFileSelect"
              />
              <svg viewBox="0 0 24 24" width="32" height="32" class="photo-upload-icon">
                <path fill="currentColor" d="M19,13H13V19H11V13H5V11H11V5H13V11H19V13Z"/>
              </svg>
              <p class="photo-upload-text">{{ t('issues.uploadPhotos') }}</p>
              <p class="photo-upload-hint">{{ t('issues.uploadPhotosHint') }}</p>
            </div>

            <!-- Selected Photos Preview -->
            <div v-if="selectedPhotos.length > 0" class="photos-preview">
              <div v-for="(photo, index) in selectedPhotos" :key="index" class="photo-preview-item">
                <img :src="photo.preview" :alt="photo.file.name" class="photo-preview-image" />
                <div class="photo-preview-info">
                  <span class="photo-preview-name">{{ photo.file.name }}</span>
                  <span class="photo-preview-size">{{ formatFileSize(photo.file.size) }}</span>
                </div>
                <button
                  type="button"
                  class="photo-preview-remove"
                  @click.stop="removePhoto(index)"
                  :title="t('common.remove')"
                >
                  <svg viewBox="0 0 24 24" width="16" height="16">
                    <path fill="currentColor" d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z"/>
                  </svg>
                </button>
              </div>
            </div>

            <!-- Upload Progress -->
            <div v-if="uploadingPhotos" class="upload-progress">
              <div class="upload-progress-bar">
                <div class="upload-progress-fill" :style="{ width: `${uploadProgress}%` }"></div>
              </div>
              <span class="upload-progress-text">{{ t('issues.uploadingPhotos', { progress: uploadProgress }) }}</span>
            </div>
          </div>

        </div>

        <!-- General Error -->
        <div v-if="errors.general" class="issue-form__error">
          <svg viewBox="0 0 24 24" width="20" height="20">
            <path fill="currentColor" d="M13 14H11V9H13M13 18H11V16H13M1 21H23L12 2L1 21Z"/>
          </svg>
          <span>{{ errors.general }}</span>
        </div>
      </form>
    </div>

    <!-- Modal Actions -->
    <template #footer>
      <div class="issue-form__actions">
        <AppButton
          type="button"
          variant="secondary"
          @click="$emit('close')"
          :disabled="saving"
          class="issue-form__btn issue-form__btn--cancel"
        >
          {{ t('common.cancel') }}
        </AppButton>
        <AppButton
          type="button"
          variant="primary"
          @click="handleSubmit"
          :loading="saving"
          :disabled="saving || !isFormValid"
          class="issue-form__btn issue-form__btn--save"
        >
          {{ isEditing ? t('common.save') : t('common.create') }}
        </AppButton>
      </div>
    </template>
  </AppModal>
</template>

<script setup lang="ts">
/// <reference types="vue" />
// @ts-ignore
import { ref, computed, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useNotifications } from '@/composables/useNotifications';

// Components
import AppModal from '../../base/Modal.vue';
import AppButton from '../../base/AppButton.vue';

// Services
import deliveryIssuesService from '@/services/deliveryIssues.service';
import issuePhotoService, { type IssuePhoto } from '@/services/issuePhoto.service';
import type { Issue, IssueStatus, IssuePriority } from '@/types/issue';

interface SelectedPhoto {
  file: File;
  preview: string;
}

interface Intervenant {
  id: number;
  name: string;
}

interface Props {
  visible: boolean;
  issue: Issue | null;
  projectId: string | number;
  intervenants: Intervenant[];
}

interface Emits {
  close: [];
  'issue-saved': [issue: Issue, isNew: boolean];
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

const { t } = useI18n();
const { notifyError } = useNotifications();

// Form data
const formData = ref({
  title: '',
  description: '',
  status: 'OPEN' as IssueStatus,
  priority: 'MEDIUM' as IssuePriority,
  createdAt: new Date().toISOString().split('T')[0],
  resolvedDate: '',
  assignedTo: null as number | null,
  resolutionNotes: ''
});

const errors = ref<Record<string, string>>({});
const saving = ref(false);

// Photo upload state
const fileInput = ref<HTMLInputElement | null>(null);
const selectedPhotos = ref<SelectedPhoto[]>([]);
const isDragging = ref(false);
const uploadingPhotos = ref(false);
const uploadProgress = ref(0);

// Computed
const isEditing = computed(() => props.issue !== null);

const showResolvedDate = computed(() => {
  return formData.value.status === 'RESOLVED' || formData.value.status === 'CLOSED';
});

const showResolutionNotes = computed(() => {
  return formData.value.status === 'RESOLVED' || formData.value.status === 'CLOSED';
});

const isFormValid = computed(() => {
  return formData.value.title.trim().length >= 3;
});

// Methods
const loadFormData = () => {
  if (props.issue) {
    formData.value = {
      title: props.issue.title || '',
      description: props.issue.description || '',
      status: props.issue.status || 'OPEN',
      priority: props.issue.priority || 'MEDIUM',
      resolvedDate: props.issue.resolvedDate || '',
      assignedTo: props.issue.assignedTo || null,
      resolutionNotes: props.issue.resolutionNotes || ''
    };
  } else {
    formData.value = {
      title: '',
      description: '',
      status: 'OPEN',
      priority: 'MEDIUM',
      createdAt: new Date().toISOString().split('T')[0],
      resolvedDate: '',
      assignedTo: null,
      resolutionNotes: ''
    };
  }
  errors.value = {};
};

const validateForm = (): boolean => {
  errors.value = {};

  if (!formData.value.title || formData.value.title.trim().length < 3) {
    errors.value.title = t('issues.errors.titleTooShort');
    return false;
  }

  if (formData.value.title.length > 100) {
    errors.value.title = t('issues.errors.titleTooLong');
    return false;
  }
  
  if (formData.value.status === 'RESOLVED' && !formData.value.resolutionNotes) {
    errors.value.resolutionNotes = t('issues.errors.resolutionNotesRequired');
    return false;
  }

  return true;
};

// Photo upload methods
const triggerFileInput = () => {
  fileInput.value?.click();
};

const handleFileSelect = (event: Event) => {
  const target = event.target as HTMLInputElement;
  if (target.files) {
    addFiles(Array.from(target.files));
  }
};

const handleFileDrop = (event: DragEvent) => {
  isDragging.value = false;
  if (event.dataTransfer?.files) {
    const imageFiles = Array.from(event.dataTransfer.files).filter(file => 
      file.type.startsWith('image/')
    );
    addFiles(imageFiles);
  }
};

const addFiles = (files: File[]) => {
  files.forEach(file => {
    // Create preview URL
    const preview = URL.createObjectURL(file);
    selectedPhotos.value.push({ file, preview });
  });
};

const removePhoto = (index: number) => {
  // Revoke the preview URL to free memory
  URL.revokeObjectURL(selectedPhotos.value[index].preview);
  selectedPhotos.value.splice(index, 1);
};

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const uploadPhotos = async (issueId: number | string): Promise<void> => {
  if (selectedPhotos.value.length === 0) return;

  uploadingPhotos.value = true;
  uploadProgress.value = 0;

  try {
    const formData = new FormData();
    selectedPhotos.value.forEach((photo: SelectedPhoto) => {
      formData.append('photos', photo.file);
    });

    await issuePhotoService.uploadIssuePhotos(
      props.projectId,
      issueId,
      formData,
      (progressEvent) => {
        if (progressEvent.total) {
          uploadProgress.value = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        }
      }
    );

    // Clear selected photos after successful upload
    selectedPhotos.value.forEach((photo: SelectedPhoto) => URL.revokeObjectURL(photo.preview));
    selectedPhotos.value = [];
  } catch (error: any) {
    console.error('Error uploading photos:', error);
    notifyError(t('issues.errors.photoUploadFailed'));
    throw error;
  } finally {
    uploadingPhotos.value = false;
    uploadProgress.value = 0;
  }
};

const handleSubmit = async () => {
  if (!validateForm()) {
    return;
  }

  saving.value = true;
  errors.value = {};

  try {
    const issueData = {
      title: formData.value.title.trim(),
      description: formData.value.description?.trim(),
      status: formData.value.status,
      priority: formData.value.priority,
      createdAt: formData.value.createdAt,
      resolvedDate: formData.value.resolvedDate || undefined,
      assignedTo: formData.value.assignedTo || undefined,
      resolutionNotes: formData.value.resolutionNotes?.trim() || undefined
    };

    let result;
    if (isEditing.value && props.issue) {
      result = await deliveryIssuesService.updateIssue(
        props.projectId.toString(),
        props.issue.id.toString(),
        issueData
      );
    } else {
      result = await deliveryIssuesService.createIssue(
        props.projectId.toString(),
        issueData
      );
    }

    if (result) {
      // Upload photos if any are selected
      if (selectedPhotos.value.length > 0) {
        try {
          await uploadPhotos(result.id);
        } catch (photoError) {
          // Issue was created but photos failed - notify user but don't fail the whole operation
          console.error('Photos upload failed after issue creation:', photoError);
        }
      }

      emit('issue-saved', result, !isEditing.value);
      emit('close');
    }
  } catch (error: any) {
    console.error('Error saving issue:', error);
    errors.value.general = error.message || t('issues.errors.saveFailed');
    notifyError(errors.value.general);
  } finally {
    saving.value = false;
  }
};

// Watchers
watch(() => props.visible, (newVal: boolean) => {
  if (newVal) {
    loadFormData();
  }
});

// Lifecycle
onMounted(() => {
  if (props.visible) {
    loadFormData();
  }
});
</script>

<style scoped>
.issue-form {
  padding: 24px 0;
}

.issue-form__grid {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.issue-form__row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.issue-form__error {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background-color: #fee2e2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  color: #991b1b;
  margin-top: 16px;
}

.issue-form__error svg {
  flex-shrink: 0;
}

/* App Form Field Styles */
.app-form-field {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.app-form-field--full {
  grid-column: 1 / -1;
}

.app-form-field__label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 500;
  font-size: 0.875rem;
  color: #374151;
}

.app-form-field__label--required::after {
  content: '*';
  color: #dc2626;
  margin-left: 2px;
}

.app-form-field__icon {
  color: #6b7280;
}

.app-form-field__wrapper {
  position: relative;
}

.app-form-field__input {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.875rem;
  transition: all 0.2s;
}

.app-form-field__input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.app-form-field__input--error {
  border-color: #dc2626;
}

.app-form-field__input--textarea {
  resize: vertical;
  min-height: 80px;
  font-family: inherit;
}

.app-form-field__input--select {
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3E%3Cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3E%3C/svg%3E");
  background-position: right 8px center;
  background-repeat: no-repeat;
  background-size: 20px;
  padding-right: 36px;
}

.app-form-field__error {
  display: flex;
  align-items: center;
  gap: 4px;
  color: #dc2626;
  font-size: 0.75rem;
}

.app-form-field__error svg {
  flex-shrink: 0;
}

@media (max-width: 768px) {
  .issue-form__row {
    grid-template-columns: 1fr;
  }
}

/* Footer actions alignment */
.issue-form__actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding-top: 12px;
}

@media (max-width: 768px) {
  .issue-form__actions {
    flex-direction: column-reverse;
  }
}

/* Photo Upload Styles */
.photo-upload-area {
  border: 2px dashed #d1d5db;
  border-radius: 8px;
  padding: 32px 16px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s;
  background-color: #f9fafb;
}

.photo-upload-area:hover {
  border-color: #3b82f6;
  background-color: #eff6ff;
}

.photo-upload-area--dragging {
  border-color: #3b82f6;
  background-color: #dbeafe;
}

.photo-upload-input {
  display: none;
}

.photo-upload-icon {
  color: #6b7280;
  margin: 0 auto 8px;
}

.photo-upload-text {
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  margin: 0 0 4px 0;
}

.photo-upload-hint {
  font-size: 0.75rem;
  color: #6b7280;
  margin: 0;
}

.photos-preview {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 12px;
}

.photo-preview-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px;
  background-color: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
}

.photo-preview-image {
  width: 48px;
  height: 48px;
  object-fit: cover;
  border-radius: 4px;
  flex-shrink: 0;
}

.photo-preview-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
}

.photo-preview-name {
  font-size: 0.875rem;
  color: #374151;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.photo-preview-size {
  font-size: 0.75rem;
  color: #6b7280;
}

.photo-preview-remove {
  flex-shrink: 0;
  padding: 4px;
  background: none;
  border: none;
  cursor: pointer;
  color: #6b7280;
  transition: color 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.photo-preview-remove:hover {
  color: #dc2626;
}

.upload-progress {
  margin-top: 12px;
}

.upload-progress-bar {
  height: 8px;
  background-color: #e5e7eb;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 4px;
}

.upload-progress-fill {
  height: 100%;
  background-color: #3b82f6;
  transition: width 0.3s ease;
}

.upload-progress-text {
  font-size: 0.75rem;
  color: #6b7280;
}
</style>
